import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { api } from '../../../../services/api';
import { useWorkspace } from '../../WorkspaceContext';
import WeeklyHoursEditor from '../../components/WeeklyHoursEditor';

const DEFAULT_SCHEDULE = {
  monday: { open: '09:00', close: '18:00', closed: false },
  tuesday: { open: '09:00', close: '18:00', closed: false },
  wednesday: { open: '09:00', close: '18:00', closed: false },
  thursday: { open: '09:00', close: '18:00', closed: false },
  friday: { open: '09:00', close: '18:00', closed: false },
  saturday: { open: '09:00', close: '17:00', closed: false },
  sunday: { open: '09:00', close: '17:00', closed: true },
};

// Per-staff weekly working hours. When a staff member has no schedule they
// simply follow the location's opening hours; a custom schedule narrows their
// bookable slots.
const ManageStaffSchedule = () => {
  const { activeLocation } = useWorkspace();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadStaff = useCallback(async () => {
    if (!activeLocation) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const result = await api.getWorkspaceStaff(activeLocation.id);
    if (result.error) {
      setError(result.error);
      setStaff([]);
    } else {
      setError('');
      setStaff(Array.isArray(result.data) ? result.data : []);
    }
    setLoading(false);
  }, [activeLocation]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    setSelectedId(null);
    setDraft(null);
  }, [activeLocation?.id]);

  // Auto-select the first staff member so mobile users aren't staring at an empty pane.
  useEffect(() => {
    if (!selectedId && staff.length > 0) {
      const first = staff[0];
      setSelectedId(first.id);
      setDraft(first.schedule || null);
    }
  }, [staff, selectedId]);

  const selected = staff.find((member) => member.id === selectedId) || null;

  const selectStaff = (member) => {
    setSelectedId(member.id);
    setDraft(member.schedule || null);
    setSaved(false);
  };

  const followsLocation = draft === null;

  const toggleCustomSchedule = () => {
    setDraft(followsLocation ? { ...(selected?.schedule || DEFAULT_SCHEDULE) } : null);
    setSaved(false);
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    const result = await api.updateWorkspaceStaff(selected.id, {
      schedule: draft,
      locationId: activeLocation?.id,
    });
    setSaving(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setSaved(true);
    await loadStaff();
  };

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-light text-ink-900 sm:text-3xl">Staff schedule</h2>
        <p className="mt-1 text-sm text-ink-700/80">
          Optional per-person hours for planning. Booking availability follows the location{' '}
          <Link to="../hours" className="underline decoration-saffron-500 decoration-2 underline-offset-4">
            working hours
          </Link>
          ; only owner blocks close individual slots.
        </p>
        </div>
        <Link
          to="../staff"
          className="inline-flex flex-none items-center justify-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
        >
          <Plus className="h-4 w-4" />
          Add staff
        </Link>
      </div>

      {error && (
        <div className="mt-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
        </div>
      ) : staff.length === 0 ? (
        <div className="mt-6 bg-sand-50 px-5 py-12 text-center ring-1 ring-ink-900/10 sm:px-6 sm:py-16">
          <p className="font-display text-2xl font-light text-ink-900">No staff yet</p>
          <p className="mt-2 text-sm text-ink-700/70">Add your team before setting schedules.</p>
          <Link
            to="../staff"
            className="mt-6 inline-flex items-center gap-2 bg-saffron-500 px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
          >
            <Plus className="h-4 w-4" />
            Add staff
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-6">
          <div className="min-w-0 bg-sand-50 ring-1 ring-ink-900/10">
            <p className="border-b border-ink-900/10 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/60 lg:hidden">
              Choose staff
            </p>
            <ul className="scrollbar-hide flex divide-x divide-ink-900/10 overflow-x-auto lg:block lg:divide-x-0 lg:divide-y">
              {staff.map((member) => (
                <li key={member.id} className="flex-none lg:block">
                  <button
                    type="button"
                    onClick={() => selectStaff(member)}
                    className={`flex w-44 items-center gap-3 px-4 py-3 text-left transition-colors lg:w-full ${
                      selectedId === member.id ? 'bg-ink-900 text-sand-50' : 'hover:bg-ink-900/5'
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-semibold ${
                        selectedId === member.id
                          ? 'bg-saffron-500 text-ink-900'
                          : 'bg-ink-900 text-saffron-400'
                      }`}
                    >
                      {(member.name || '?')[0].toUpperCase()}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{member.name}</span>
                      <span
                        className={`block truncate text-xs ${
                          selectedId === member.id ? 'text-sand-200/70' : 'text-ink-600/60'
                        }`}
                      >
                        {member.schedule ? 'Custom' : 'Location hours'}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="min-w-0">
            {!selected ? (
              <div className="bg-sand-50 px-5 py-12 text-center ring-1 ring-ink-900/10">
                <p className="text-sm text-ink-700/70">Choose a staff member to edit their hours.</p>
              </div>
            ) : (
              <div>
                <div className="flex flex-col gap-3 bg-sand-50 px-4 py-4 ring-1 ring-ink-900/10 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink-900">{selected.name}</p>
                    <p className="mt-0.5 text-sm text-ink-700/70">
                      {followsLocation
                        ? 'Follows the location opening hours'
                        : 'Works a custom weekly schedule'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={toggleCustomSchedule}
                    className="flex-none px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/20 transition-colors hover:bg-white"
                  >
                    {followsLocation ? 'Set custom schedule' : 'Follow location hours'}
                  </button>
                </div>

                {!followsLocation && (
                  <div className="mt-4 min-w-0">
                    <WeeklyHoursEditor
                      value={draft}
                      onChange={(next) => {
                        setDraft(next);
                        setSaved(false);
                      }}
                    />
                  </div>
                )}

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save schedule'}
                  </button>
                  {saved && <p className="text-sm text-ink-700/70">Schedule saved.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStaffSchedule;
